import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductDetail } from '../../types/product';
import { FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { formatCurrency } from '../../utils/currency';
import { getLocalizedField } from '../../utils/localize';
import type { SupportedLanguage } from '../../i18n';
import CCOptionsEditModal from '../Cart/CCOptionsEditModal';

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ListItem = styled.article`
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 200px;
  background-color: var(--gray-light);
  overflow: hidden;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: 200px;
    height: 150px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ListItemContent = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductHeader = styled.div`
  margin-bottom: 0.5rem;
`;

const ProductName = styled.h3`
  font-size: 1.2rem;
  color: var(--text-color);
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ProductDescription = styled.p`
  font-size: 0.95rem;
  color: var(--gray-dark);
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ListItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid var(--gray-light);

  @media (min-width: 768px) {
    border-top: none;
    padding-top: 0;
  }
`;

const Price = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--primary-color);
`;

const AddToCartButton = styled.button`
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background-color: var(--secondary-color);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyMessage = styled.p`
  text-align: center;
  color: var(--gray-dark);
  font-size: 1.2rem;
  padding: 3rem 1rem;
`;

const Skeleton = styled.div`
  background: linear-gradient(
    90deg,
    var(--gray-light) 0%,
    #f0f0f0 50%,
    var(--gray-light) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

const SkeletonItem = styled(ListItem)`
  cursor: default;

  &:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

interface ProductListProps {
  products: ProductDetail[];
  loading?: boolean;
}

const ProductList: React.FC<ProductListProps> = ({ products, loading }) => {
  const safeProducts = Array.isArray(products) ? products : [];
  const { addToCart } = useCart();
  const { t, i18n } = useTranslation();
  // Mirrors the LanguageToggle.tsx pattern: i18next's language can be a
  // region-qualified tag or unset, so narrow to the two v1-supported values.
  const language: SupportedLanguage = (i18n.language as SupportedLanguage) === 'zh' ? 'zh' : 'en';
  const [addingProducts, setAddingProducts] = useState<Set<number>>(new Set());
  const [sizePickerProduct, setSizePickerProduct] = useState<ProductDetail | null>(null);

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>, product: ProductDetail) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent double clicks
    if (addingProducts.has(product.product_id)) {
      return;
    }

    // Products with a REQUIRED option group must have a selection made
    // before being added to cart (the server rejects the line otherwise).
    // Quick-add is only for products with no required group.
    if (product.has_required_options) {
      setSizePickerProduct(product);
      return;
    }

    setAddingProducts(prev => new Set(prev).add(product.product_id));
    addToCart(product, 1);

    // Reset the flag after a short delay
    setTimeout(() => {
      setAddingProducts(prev => {
        const next = new Set(prev);
        next.delete(product.product_id);
        return next;
      });
    }, 500);
  };

  if (loading) {
    return (
      <ListContainer>
        {[...Array(4)].map((_, index) => (
          <SkeletonItem key={index}>
            <Skeleton style={{ width: '100%', height: '200px' }} />
            <ListItemContent>
              <Skeleton style={{ height: '1.5rem', marginBottom: '0.5rem' }} />
              <Skeleton style={{ height: '3rem', marginBottom: '1rem' }} />
              <ListItemFooter>
                <Skeleton style={{ height: '2rem', width: '80px' }} />
                <Skeleton style={{ height: '48px', width: '120px' }} />
              </ListItemFooter>
            </ListItemContent>
          </SkeletonItem>
        ))}
      </ListContainer>
    );
  }

  if (safeProducts.length === 0) {
    return <EmptyMessage>{t('products.noResults')}</EmptyMessage>;
  }

  return (
    <>
      <ListContainer>
        {safeProducts.map((product) => (
          <ListItem key={product.product_id} as={Link} to={`/products/${product.product_id}`}>
            <ImageWrapper>
              <ProductImage
                src={
                  product.images && product.images.length > 0
                    ? product.images.find(img => img.is_primary)?.image_url || product.images[0].image_url
                    : "https://images.unsplash.com/photo-1578985545062-69928b1d9587"
                }
                alt={getLocalizedField(product, 'name', language) as string}
                loading="lazy"
              />
            </ImageWrapper>
            <ListItemContent>
              <ProductHeader>
                <ProductName>{getLocalizedField(product, 'name', language) as string}</ProductName>
                <ProductDescription>{getLocalizedField(product, 'description', language) as string | undefined}</ProductDescription>
              </ProductHeader>
              <ListItemFooter>
                <Price>{formatCurrency(product.base_price)}</Price>
                <AddToCartButton
                  type="button"
                  onClick={(e) => handleAddToCart(e, product)}
                  disabled={addingProducts.has(product.product_id)}
                >
                  <FiShoppingCart />
                  <span>{addingProducts.has(product.product_id) ? t('products.adding') : t('productDetail.addToCart')}</span>
                </AddToCartButton>
              </ListItemFooter>
            </ListItemContent>
          </ListItem>
        ))}
      </ListContainer>
      {sizePickerProduct && (
        <CCOptionsEditModal
          item={{ product: sizePickerProduct, quantity: 1 }}
          onConfirm={(ccOptions, price) => {
            addToCart(sizePickerProduct, 1, undefined, price, undefined, undefined, ccOptions);
            setSizePickerProduct(null);
          }}
          onClose={() => setSizePickerProduct(null)}
        />
      )}
    </>
  );
};

export default ProductList;